import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const TenantContext = createContext(null);

// Feature flags by subscription tier
const PLAN_FEATURES = {
  STARTER: {
    loyalty: false,
    delivery: false,
    kitchenDisplay: false,
    advancedReporting: false,
    advancedAnalytics: false,
    staffManagement: false,
    inventory: false,
    tableManagement: false,
    orders: false,
    customLogo: false,
    maxTables: 10,
    maxUsers: 2,
    maxItems: 50,
  },
  PRO: {
    loyalty: true,
    delivery: true,
    kitchenDisplay: false,
    advancedReporting: true,
    advancedAnalytics: true,
    staffManagement: true,
    inventory: true,
    tableManagement: true,
    orders: true,
    customLogo: true,
    maxTables: 50,
    maxUsers: 10,
    maxItems: 500,
  },
  ENTERPRISE: {
    loyalty: true,
    delivery: true,
    kitchenDisplay: true,
    advancedReporting: true,
    advancedAnalytics: true,
    staffManagement: true,
    inventory: true,
    tableManagement: true,
    orders: true,
    customLogo: true,
    maxTables: 999,
    maxUsers: 999,
    maxItems: 9999,
  },
};

export const TenantProvider = ({ children, token }) => {
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (token) {
      fetchTenantInfo();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchTenantInfo = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/auth/me', {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      // Get tenant info if available (slug is required for public links)
      const userTenantId = response.data.tenant_id || null;
      const userTenantSlug = response.data.tenant_slug || null;
      
      // Fetch full tenant document to get logo_url, name, and settings
      let logoUrl = null;
      let businessName = 'My Business';
      let tenantSettings = null;
      if (userTenantId) {
        try {
          const tenantDocResponse = await axios.get(`/api/tenants/${userTenantId}/info`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          logoUrl = tenantDocResponse.data.logo_url || null;
          businessName = tenantDocResponse.data.name || 'My Business';
          tenantSettings = tenantDocResponse.data.settings || null;
        } catch (err) {
          console.error('Error fetching tenant document:', err);
        }
      }
      
      // For now, use a default tenant setup
      // In production, fetch from /api/tenants/{tenant_id}
      const tenantInfo = {
        id: userTenantId,
        slug: userTenantSlug,
        name: businessName,
        plan: response.data.plan || 'STARTER',
        logo_url: logoUrl,
        logo: logoUrl, // Keep for backward compatibility
        primaryColor: response.data.primaryColor || '#3b82f6',
        settings: tenantSettings,
        supportedLanguages: tenantSettings?.supported_languages || ['tr'],
        features: PLAN_FEATURES[response.data.plan || 'STARTER'],
        user: response.data,
      };
      
      setTenant(tenantInfo);
    } catch (err) {
      console.error('Error fetching tenant info:', err);
      // Set default tenant on error
      setTenant({
        id: null,
        slug: null,
        name: 'My Business',
        plan: 'STARTER',
        logo: null,
        primaryColor: '#3b82f6',
        features: PLAN_FEATURES.STARTER,
        settings: null,
        supportedLanguages: ['tr'],
      });
    } finally {
      setLoading(false);
    }
  };

  const isFeatureEnabled = (featureName) => {
    if (!tenant) return false;
    return tenant.features[featureName] === true;
  };

  const canCreateUser = (currentUserCount) => {
    if (!tenant) return false;
    return currentUserCount < tenant.features.maxUsers;
  };

  const canCreateTable = (currentTableCount) => {
    if (!tenant) return false;
    return currentTableCount < tenant.features.maxTables;
  };

  const canCreateMenuItem = (currentItemCount) => {
    if (!tenant) return false;
    return currentItemCount < tenant.features.maxItems;
  };

  const value = {
    tenant,
    loading,
    error,
    isFeatureEnabled,
    canCreateUser,
    canCreateTable,
    canCreateMenuItem,
    fetchTenantInfo,
    PLAN_FEATURES,
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
};

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within TenantProvider');
  }
  return context;
};

export default TenantContext;
